import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import { get, useFormContext, useWatch } from 'react-hook-form';
import type { CustomQuestionsFormData } from '../types';
import { labelToIdentifier } from '../utils/label-to-identifier';

// The DI a label auto-derives to: a `custom.*` field, or empty while the label is blank.
const autoDataIdentifier = (label: string): string => {
  const slug = labelToIdentifier(label);
  return slug ? `custom.${slug}` : '';
};

/**
 * Hook that auto-generates the dataIdentifier field from the label field.
 * Stops auto-generating once the user manually edits the dataIdentifier.
 */
export const useAutoDataIdentifier = questionIndex => {
  const $ = _c(20);
  const {
    control,
    setValue,
    formState: t0
  } = useFormContext();
  const {
    errors
  } = t0;
  const t1 = `questions.${questionIndex}.label`;
  let t2;
  if ($[0] !== control || $[1] !== t1) {
    t2 = {
      control,
      name: t1
    };
    $[0] = control;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const label = useWatch(t2);
  const t3 = `questions.${questionIndex}.dataIdentifier`;
  let t4;
  if ($[3] !== control || $[4] !== t3) {
    t4 = {
      control,
      name: t3
    };
    $[3] = control;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const dataIdentifier = useWatch(t4);
  const t5 = `questions.${questionIndex}.dataIdentifier`;
  let t6;
  if ($[6] !== errors || $[7] !== t5) {
    t6 = get(errors, t5);
    $[6] = errors;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const hasError = !!t6;
  const lastAutoGeneratedRef = useRef("");
  const hasManuallyEditedRef = useRef(false);
  let t7;
  if ($[9] !== dataIdentifier || $[10] !== label) {
    t7 = () => {
      if (dataIdentifier && dataIdentifier !== autoDataIdentifier(label || "")) {
        hasManuallyEditedRef.current = true;
      }
    };
    $[9] = dataIdentifier;
    $[10] = label;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = [];
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[13] !== dataIdentifier || $[14] !== hasError || $[15] !== label || $[16] !== questionIndex || $[17] !== setValue) {
    t9 = () => {
      if ((dataIdentifier ?? "").startsWith("id.")) {
        return;
      }
      if (hasManuallyEditedRef.current) {
        return;
      }
      if (hasError && dataIdentifier !== lastAutoGeneratedRef.current) {
        hasManuallyEditedRef.current = true;
        return;
      }
      const autoGenerated = autoDataIdentifier(label || "");
      if (dataIdentifier !== autoGenerated) {
        if (dataIdentifier === lastAutoGeneratedRef.current) {
          setValue(`questions.${questionIndex}.dataIdentifier` as const, autoGenerated, {
            shouldValidate: hasError,
            shouldDirty: true
          });
          lastAutoGeneratedRef.current = autoGenerated;
          return;
        }
        hasManuallyEditedRef.current = true;
        return;
      }
      lastAutoGeneratedRef.current = autoGenerated;
    };
    t10 = [label, dataIdentifier, questionIndex, setValue, hasError];
    $[13] = dataIdentifier;
    $[14] = hasError;
    $[15] = label;
    $[16] = questionIndex;
    $[17] = setValue;
    $[18] = t10;
    $[19] = t9;
  } else {
    t10 = $[18];
    t9 = $[19];
  }
  useEffect(t9, t10);
};